import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme, ThemeOptions } from '@mui/material';
import { AppType, useAponoAuth } from '@common/ui/Authenticated';
import { IframeMessage, MessageType } from '@common/ui/Authenticated/interfaces';

import { aponoTheme } from './aponoTheme';
import { msTeamsTheme } from './teamsTheme';
import { useThemeMode } from '@common/hooks/use-theme-mode.hook.ts';
import { useTeamsThemeMode } from '@common/hooks/use-teams-theme-mode.ts';

interface BackstageTheme extends ThemeOptions {
  palette?: ThemeOptions['palette'] & { type?: 'light' | 'dark' };
}

export interface ThemeProps {
  isDark?: boolean;
}

function ThemeProviderPortal({ isDark, children }: PropsWithChildren<ThemeProps>) {
  const { prefersDarkMode } = useThemeMode(isDark);
  const theme = aponoTheme(prefersDarkMode);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

function ThemeProviderTeams({ isDark, children }: PropsWithChildren<ThemeProps>) {
  const { prefersDarkMode } = useTeamsThemeMode(isDark);
  const theme = msTeamsTheme(prefersDarkMode);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

function ThemeProviderBackstage({ isDark, children }: PropsWithChildren<ThemeProps>) {
  const [parentTheme, setParentTheme] = useState<BackstageTheme | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<IframeMessage>) => {
      if (event.data.type === MessageType.THEME_UPDATE && event.data.theme) {
        setParentTheme(event.data.theme);
      }
    };

    window.parent.postMessage({ type: MessageType.THEME_READY }, '*');
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const theme = useMemo(() => {
    const shouldUseDarkMode = parentTheme?.palette?.type === 'dark' || isDark;
    const baseTheme = aponoTheme(shouldUseDarkMode);
    if (!parentTheme) return baseTheme;

    const combinedTheme: ThemeOptions = {
      ...parentTheme,
      palette: {
        ...parentTheme.palette,
        border: baseTheme.palette.border,
        graph: baseTheme.palette.graph,
        button: baseTheme.palette.button,
      },
      components: {
        ...parentTheme.components,
        MuiAvatar: baseTheme?.components?.MuiAvatar,
        MuiLink: baseTheme?.components?.MuiLink,
        MuiButton: {
          ...parentTheme?.components?.MuiButton,
          variants: baseTheme?.components?.MuiButton?.variants,
        },
      },
    };

    return createTheme(combinedTheme);
  }, [parentTheme, isDark]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export function AppTheme({ isDark, children }: PropsWithChildren<ThemeProps>) {
  const { user } = useAponoAuth();

  switch (user.app) {
    case AppType.Teams:
      return <ThemeProviderTeams isDark={isDark}>{children}</ThemeProviderTeams>;
    case AppType.Backstage:
      return <ThemeProviderBackstage isDark={isDark}>{children}</ThemeProviderBackstage>;
    default:
      return <ThemeProviderPortal isDark={isDark}>{children}</ThemeProviderPortal>;
  }
}
